import { Link, NavLink, Navigate, Outlet, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, FileDown, Loader2 } from 'lucide-react';
import { CURRENT_SCAN_KEY, loadPersistedScan } from '../lib/api';
import { exportScanPdf } from '../lib/pdf';
import type { ScanResult } from '../types';
import { createContext, useContext, useEffect, useState } from 'react';
import { Footer } from '../components/Footer';
import { ThemeToggle } from '../components/ThemeToggle';

const ScanCtx = createContext<ScanResult | null>(null);
export const useScan = () => {
  const v = useContext(ScanCtx);
  if (!v) throw new Error('useScan must be used inside ScanLayout');
  return v;
};

export function ScanLayout() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(false);

  // Scan result lives in React Query's in-memory cache, with a sessionStorage
  // fallback so a refresh / back-button rehydrates the report instead of
  // bouncing the user home. (Tab-scoped; nothing leaves the browser.)
  const cached = qc.getQueryData<ScanResult>(CURRENT_SCAN_KEY);
  const scan = cached ?? loadPersistedScan();
  useEffect(() => {
    if (!cached && scan) qc.setQueryData(CURRENT_SCAN_KEY, scan);
  }, [cached, scan, qc]);
  if (!scan) return <Navigate to="/" replace />;

  return (
    <ScanCtx.Provider value={scan}>
      <div className="app-bg flex min-h-screen flex-col text-ink">
        <TopBar
          domain={scan.domain}
          createdAt={scan.created_at}
          onNew={() => navigate('/')}
          onToggleNav={() => setNavOpen(o => !o)}
          navOpen={navOpen}
        />

        <div className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)] xl:grid-cols-[220px_minmax(0,1fr)_280px]">
            <Sidebar mobileOpen={navOpen} onClose={() => setNavOpen(false)} />
            <main className="min-w-0 space-y-4">
              {/* Mobile-only domain + freshness badge. The top bar hides
                  these below md, so without this the user can read the
                  results without ever knowing which domain or how stale
                  the scan is. Hidden on md+ where the top bar shows them. */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-3 md:hidden">
                <span className="inline-flex items-center gap-1.5 font-medium text-ink-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  {scan.domain}
                </span>
                <span aria-hidden="true">·</span>
                <FreshnessLabel ts={scan.created_at} />
              </div>
              <AlertsSummary />
              <Outlet />
            </main>
            <RightRail />
          </div>
        </div>
        <Footer />
      </div>
    </ScanCtx.Provider>
  );
}

// ---------- Top bar ----------
function TopBar({
  domain, createdAt, onNew, onToggleNav, navOpen
}: {
  domain: string;
  createdAt: number;
  onNew: () => void;
  onToggleNav: () => void;
  navOpen: boolean;
}) {
  return (
    <header className="no-print sticky top-0 z-40 border-b border-line bg-canvas/85 backdrop-blur-xl">
      <div className="mx-auto flex max-w-[1400px] items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
        <button
          onClick={onToggleNav}
          aria-label="Toggle navigation"
          className="grid h-11 w-11 place-items-center rounded-md text-ink-2 hover:bg-panel-2 lg:hidden"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {navOpen
              ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
              : <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>}
          </svg>
        </button>

        <Link to="/" className="flex items-center gap-2">
          <Logo size={28} />
          <span className="font-display text-lg font-bold tracking-tight text-ink">PROTBYTE <span className="text-accent">Sentinel</span></span>
        </Link>

        <div className="ml-auto hidden items-center gap-3 md:flex">
          <span className="inline-flex items-center gap-2 text-sm">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            <span className="font-medium text-ink-2">{domain}</span>
          </span>
          <span className="text-xs text-ink-3" aria-hidden="true">·</span>
          <FreshnessLabel ts={createdAt} className="text-xs text-ink-3" />
        </div>

        <div className="ml-auto flex items-center gap-2 md:ml-4">
          <ThemeToggle />
          <ExportPdfButton />
          <button
            onClick={onNew}
            aria-label="New scan"
            title="New scan"
            className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-md border border-line-2 bg-panel px-3 py-1.5 text-sm font-medium text-ink-2 hover:bg-panel-2"
          >
            <Plus className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
            <span className="hidden sm:inline">New Scan</span>
          </button>
        </div>
      </div>
    </header>
  );
}

// ---------- Export PDF ----------
// Generates the complete report as a PDF entirely in the browser (jsPDF +
// autotable are loaded on demand), so the scan result still never leaves the
// machine.
function ExportPdfButton() {
  const scan = useScan();
  const [status, setStatus] = useState<'idle' | 'busy' | 'error'>('idle');

  const onExport = async () => {
    if (status === 'busy') return;
    setStatus('busy');
    try {
      await exportScanPdf(scan);
      setStatus('idle');
    } catch (err) {
      console.error('PDF export failed', err);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  const label = status === 'busy' ? 'Exporting…' : status === 'error' ? 'Export failed' : 'Export PDF';
  return (
    <button
      onClick={onExport}
      disabled={status === 'busy'}
      aria-label="Export the full report as PDF"
      title="Export the full report as a PDF"
      className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-md border border-line-2 bg-panel px-3 py-1.5 text-sm font-medium text-ink-2 hover:bg-panel-2 disabled:opacity-60"
    >
      {status === 'busy'
        ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" strokeWidth={2.25} aria-hidden="true" />
        : <FileDown className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

// ---------- "Scanned 30s ago" — re-renders every 15s so it stays current ----------
function FreshnessLabel({ ts, className = '' }: { ts: number; className?: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, []);
  return <span className={className}>Scanned {relativeAgo(now - ts)}</span>;
}

function relativeAgo(diffMs: number): string {
  const sec = Math.floor(diffMs / 1000);
  if (sec < 10)        return 'just now';
  if (sec < 60)        return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60)        return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24)          return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// ---------- Sidebar ----------
function Sidebar({ mobileOpen, onClose }: { mobileOpen: boolean; onClose: () => void }) {
  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/40 lg:hidden"
          onClick={onClose}
        />
      )}
      <aside
        className={
          'z-40 ' +
          (mobileOpen
            ? 'fixed inset-y-0 left-0 w-64 overflow-y-auto bg-panel p-4 pt-20 shadow-xl lg:static lg:block lg:w-auto lg:p-0 lg:pt-0 lg:shadow-none'
            : 'hidden lg:block')
        }
      >
        <div className="lg:sticky lg:top-20" onClick={onClose}>
          <NavGroup title="Security Analysis">
            <SidebarLink to="executive">Executive Dashboard</SidebarLink>
            <SidebarLink to="dns">DNS Security</SidebarLink>
            <SidebarLink to="email">Email Security</SidebarLink>
            <SidebarLink to="web">Web Security</SidebarLink>
            <SidebarLink to="ssl">SSL/TLS</SidebarLink>
          </NavGroup>
          <NavGroup title="Advanced Intel">
            <SidebarLink to="vuln">Exposure & Vulns</SidebarLink>
            <SidebarLink to="infra">Infrastructure</SidebarLink>
            <SidebarLink to="compliance">Compliance</SidebarLink>
          </NavGroup>
          <NavGroup title="Discovery">
            <SidebarLink to="subdomains">Subdomains</SidebarLink>
            <SidebarLink to="tech">Tech Stack</SidebarLink>
            <SidebarLink to="whois">WHOIS</SidebarLink>
          </NavGroup>
        </div>
      </aside>
    </>
  );
}

function NavGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-5">
      <h3 className="mb-1.5 px-3 text-[11px] font-bold uppercase tracking-wider text-accent">
        {title}
      </h3>
      <nav className="space-y-0.5">{children}</nav>
    </div>
  );
}

function SidebarLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => 'nav-link ' + (isActive ? 'nav-link-active' : '')}
    >
      {children}
    </NavLink>
  );
}

// ---------- Security alert card (shared by the right rail + in-column summary) ----------
// Each severity keys a tint utility (theme-aware) + the matching severity
// token for the chip. Critical reads a shade heavier than high.
const ALERT_STYLES: Record<string, { box: string; sev: string; strong?: boolean }> = {
  critical: { box: 'tint-bad', sev: '--t-sev-crit', strong: true },
  high:     { box: 'tint-bad', sev: '--t-sev-crit' },
  medium:   { box: 'tint-warn', sev: '--t-sev-med' },
  low:      { box: 'tint-info', sev: '--t-sev-low' },
};

function AlertCard({ a }: { a: ScanResult['alerts'][number] }) {
  const s = ALERT_STYLES[a.severity] ?? ALERT_STYLES.low;
  return (
    <div className={`rounded-xl border p-3.5 text-sm shadow-xs ${s.box}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className="rounded-sm px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white"
          style={{ background: `color-mix(in oklab, var(${s.sev}) ${s.strong ? 100 : 78}%, black)` }}
        >
          {a.severity}
        </span>
        <span className="rounded-sm bg-panel-2 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-ink-2">{a.module}</span>
      </div>
      <div className="mt-1.5 text-sm font-semibold text-ink" style={{ color: `color-mix(in oklab, var(${s.sev}) 55%, var(--t-ink))` }}>{a.title}</div>
      {a.description && (
        <div className="mt-1 text-xs leading-relaxed text-ink-2">{a.description}</div>
      )}
    </div>
  );
}

// ---------- Right rail (security alerts, xl+ only) ----------
function RightRail() {
  const scan = useScan();
  return (
    <aside className="hidden xl:block">
      <div className="sticky top-20 space-y-3">
        <h3 className="px-1 text-[11px] font-bold uppercase tracking-wider text-ink-2">
          Security Alerts
        </h3>
        {scan.alerts.length === 0 ? (
          <div className="panel p-4 text-sm text-ink-2">
            <span className="text-emerald-600">●</span> No active alerts.
          </div>
        ) : (
          scan.alerts.map((a, i) => <AlertCard key={i} a={a} />)
        )}
      </div>
    </aside>
  );
}

// In-column alerts, shown ONLY when the right rail is hidden (below xl) so the
// most important output never disappears on laptops / tablets / phones.
function AlertsSummary() {
  const scan = useScan();
  if (scan.alerts.length === 0) return null;
  return (
    <section className="panel p-4 xl:hidden">
      <h3 className="mb-3 text-[11px] font-bold uppercase tracking-wider text-ink-2">
        Security Alerts ({scan.alerts.length})
      </h3>
      <div className="space-y-2">
        {scan.alerts.map((a, i) => <AlertCard key={i} a={a} />)}
      </div>
    </section>
  );
}

// ---------- Logo (uses the user's PNG) ----------
export function Logo({ size = 28 }: { size?: number }) {
  return (
    <img
      src="/logo.png"
      alt="PROTBYTE Sentinel"
      width={size}
      height={size}
      className="select-none"
      draggable={false}
    />
  );
}
